import { useAuth } from "@/lib/auth";
import { isBrowserAuthHandoff, isDesktopShell } from "@/lib/desktop-shell";
import { GlassCard } from "@/components/glass-card";
import { ChatShot } from "@/components/product-shots";
import { PasswordAuthForm } from "@/components/password-auth-form";
import { SignInButton } from "@/components/sign-in-button";
import { MarketingShell } from "@/components/marketing-shell";
import { safeAppPath } from "@prime-assistant/shared";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { shouldTryWannlabsSsoOnLogin } from "@/lib/wannlabs-sso";
import { startWannlabsSso } from "@/lib/wannlabs-pkce";

export default function LoginPage() {
  const { user, status, googleConfigured, demoAllowed } = useAuth();
  const [params] = useSearchParams();
  const dest = safeAppPath(params.get("next"));
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  useEffect(() => {
    if (isDesktopShell() || isBrowserAuthHandoff() || status !== "unauthenticated") return;
    if (shouldTryWannlabsSsoOnLogin(window.location)) {
      setSsoRedirecting(true);
      void startWannlabsSso(dest).catch(() => setSsoRedirecting(false));
    }
  }, [status, dest]);
  if (status === "authenticated" && user && !isBrowserAuthHandoff()) {
    return <Navigate to={dest} replace />;
  }
  if (ssoRedirecting) {
    return null;
  }
  const authError =
    params.get("error") === "AccountLocked"
      ? "This account is locked. Contact your Prime administrator."
      : params.get("error") === "Configuration" || params.get("error") === "OAuthCallback"
      ? "Google sign-in failed. Close old Google tabs, then try Sign in with Google again from this page."
      : params.get("error")
        ? "Sign-in failed. Try again from this page (do not reuse an old Google link)."
        : null;

  return (
    <MarketingShell
      googleConfigured={googleConfigured}
      demoAllowed={demoAllowed}
      narrow
    >
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
        <GlassCard>
          <p className="text-sm font-medium text-teal-800">Welcome back</p>
          <h1 className="mt-2 font-[family-name:var(--font-display)] text-3xl font-semibold tracking-tight text-slate-900">
            Sign in
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Sign in with the same email and password you use for Client Hub, a passkey, or Google.
          </p>
          {isDesktopShell() && !isBrowserAuthHandoff() ? (
            <p className="mt-3 text-sm text-slate-600">
              Google and passkeys open in your browser. After you sign in, this
              app comes back to the front.
            </p>
          ) : null}
          {isBrowserAuthHandoff() ? (
            <p className="mt-3 text-sm text-slate-600">
              Finish Google or your passkey here. Mortgage Broker Assistant will open when
              you are signed in.
            </p>
          ) : null}
          {authError ? (
            <p className="mt-4 text-sm text-red-700" role="alert">
              {authError}
            </p>
          ) : null}
          <div className="mt-8 space-y-4">
            <PasswordAuthForm
              mode="login"
              redirectTo={dest}
              defaultEmail={params.get("email") ?? ""}
            />
            <div className="border-t border-white/60 pt-4">
              <SignInButton
                googleConfigured
                demoAllowed={false}
                label="Sign in with Google"
                redirectTo={dest}
              />
            </div>
            {demoAllowed ? (
              <p className="text-xs text-slate-500">
                Local demo sign-in is still available from Create an account.
              </p>
            ) : null}
          </div>
          <p className="mt-8 text-sm text-slate-600">
            New here?{" "}
            <Link
              to="/signup"
              className="font-semibold text-teal-800 underline"
            >
              Create an account
            </Link>
          </p>
          {isDesktopShell() ? null : (
            <p className="mt-2 text-sm">
              <Link to="/" className="text-slate-500 underline">
                Back to home
              </Link>
            </p>
          )}
        </GlassCard>

        <div>
          <p className="mb-3 text-sm font-medium text-teal-900">
            Pick up where you left off
          </p>
          <ChatShot />
        </div>
      </div>
    </MarketingShell>
  );
}
